import React, { useMemo } from 'react';

import { ITab } from '@components/Common';
import Preloader from '@components/Common/Preloader';
import { PageLayout } from '@components/Layout';
import { ConfirmModal } from '@components/Modals';
import { FuturesTab, SpotTab, WalletLayout } from '@components/Wallet';
import { useCloseAllOrdersAndPositionsMutation } from '@hooks/mutations/useCloseAllOrdersAndPositionsMutation';
import { useFuturesPnlForTodayQuery } from '@hooks/queries/useFuturesPnlForTodayQuery';
import { useConfirm } from '@hooks/useConfirm';
import { useText } from '@hooks/useText';
import { useWalletRealtime } from '@hooks/useWalletRealtime';
import { Typography } from '@material-tailwind/react';

import { WALLET_TYPE } from '../../constants';

const WalletPage = () => {
  const { t } = useText();
  const {
    spotAssets,
    futuresPositions,
    futuresBalance,
    walletBalance,
    spotAssetsLoading,
    positionsLoading,
    futuresBalanceLoading,
  } = useWalletRealtime();
  const { data: futuresPnl, isLoading: isFuturesPnlLoading } = useFuturesPnlForTodayQuery();
  const { mutate: closeAllOrdersAndPositions, isLoading: isAllOrdersAndPositionsClosing } =
    useCloseAllOrdersAndPositionsMutation();

  const { confirm: confirmCloseAllOrdersAndPositions, ...confirmProps } = useConfirm(
    closeAllOrdersAndPositions,
  );

  const tabs: ITab[] = useMemo(
    () => [
      {
        label: t('wallet.futuresUSDM'),
        value: WALLET_TYPE.USDT_FUTURE,
        content: (
          <FuturesTab
            onCloseAll={confirmCloseAllOrdersAndPositions}
            isAllClosing={isAllOrdersAndPositionsClosing}
            balance={futuresBalance}
            positions={futuresPositions}
            isPositionsLoading={positionsLoading}
            isBalanceLoading={futuresBalanceLoading}
          />
        ),
      },
      {
        label: t('wallet.spot'),
        value: WALLET_TYPE.SPOT,
        content: (
          <SpotTab
            onCloseAll={confirmCloseAllOrdersAndPositions}
            isAllClosing={isAllOrdersAndPositionsClosing}
            assets={spotAssets}
            isAssetsLoading={spotAssetsLoading}
          />
        ),
      },
    ],
    [
      t,
      confirmCloseAllOrdersAndPositions,
      isAllOrdersAndPositionsClosing,
      futuresBalance,
      futuresPositions,
      positionsLoading,
      futuresBalanceLoading,
      spotAssets,
      spotAssetsLoading,
    ],
  );

  if (spotAssetsLoading || futuresBalanceLoading || positionsLoading || isFuturesPnlLoading) {
    return <Preloader />;
  }

  if (!futuresBalance || !futuresPnl) {
    return null;
  }

  return (
    <PageLayout title={t('wallet.overview')}>
      <WalletLayout
        tabs={tabs}
        balance={walletBalance}
        futuresPnl={futuresPnl}
        onCloseAll={confirmCloseAllOrdersAndPositions}
        isAllClosing={isAllOrdersAndPositionsClosing}
      />
      <ConfirmModal {...confirmProps} title={t('common.confirmAction')}>
        <Typography variant="small" className="font-semibold">
          {t('wallet.closeAllConfirm')}
        </Typography>
      </ConfirmModal>
    </PageLayout>
  );
};

export default React.memo(WalletPage);
